import { useState, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import workspaceWhiteLabelService from '../../services/workspaceWhiteLabelService';
import { showSnackBar } from '../../redux/Slices/snackbarSlice';
import { optimisticUpdateWorkspace } from '../../redux/Slices/workspaceSlice';
import { useSocketAction } from '../../hooks/useSocketManager';
import { ActionTypes } from '../../sockets/ActionTypes';
import SearchSelect from '../Global/SearchSelect';
import './scss/WorkspaceWhiteLabelConfig.scss';

const WorkspaceWhiteLabelConfig = ({ workspace, currentUser }) => {
  const dispatch = useDispatch();
  const user = useSelector(state => state.user.user);

  const [saving, setSaving] = useState(false);
  const [availableInstances, setAvailableInstances] = useState([]);
  const [currentBinding, setCurrentBinding] = useState(null);
  const [selectedInstance, setSelectedInstance] = useState(null);
  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const [pendingInstance, setPendingInstance] = useState(null);
  const [canEditBinding, setCanEditBinding] = useState(false);

  // Check if current user is admin
  const isAdmin = workspace?.members?.some(
    member => member.user?._id === currentUser?._id && member.role === 'admin'
  );

  useEffect(() => {
    if (workspace) {
      loadAvailableInstances();
    }
  }, [workspace?.shortId]);

  // Real-time updates for workspace white label binding changes
  useSocketAction(ActionTypes.WORKSPACE_UPDATED, (data) => {
    // Only process events for this workspace
    if (data.workspaceId !== workspace?.shortId) return;

    // Skip events triggered by current user (already handled optimistically)
    if (data.reqUser === user?.user?._id) return;

    // Check if white label binding was updated
    if (data.updatedFields?.whiteLabelBinding !== undefined) {
      const newBinding = data.updatedFields.whiteLabelBinding;

      // Update local state
      setCurrentBinding(newBinding);

      if (newBinding?.instanceId) {
        const instance = availableInstances.find(i => i._id === newBinding.instanceId);
        if (instance) {
          setSelectedInstance({
            value: instance._id,
            label: `${instance.appName || instance.name}${instance.customDomain ? ` (${instance.customDomain})` : ` (${instance.subdomain}.zoobbe.com)`}`
          });
        }
      } else {
        setSelectedInstance({ value: 'none', label: 'None (Default Zoobbe)' });
      }

      // Update Redux state
      dispatch(optimisticUpdateWorkspace({
        workspaceId: workspace._id,
        updates: { whiteLabelBinding: newBinding }
      }));

      // Show notification
      const instanceName = availableInstances.find(i => i._id === newBinding?.instanceId)?.appName || 'None';
      dispatch(showSnackBar({
        message: `White label binding updated to: ${instanceName}`,
        type: 'info'
      }));
    }
  }, [workspace?.shortId, workspace?._id, user?.user?._id, availableInstances]);

  const loadAvailableInstances = async () => {
    try {
      const data = await workspaceWhiteLabelService.getAvailableInstances(workspace.shortId);

      setAvailableInstances(data.instances || []);
      setCurrentBinding(data.currentBinding);
      setCanEditBinding(data.canEditBinding || false);

      // Set selected value based on current binding
      if (data.currentBinding?.instanceId) {
        const instance = data.instances.find(i => i._id === data.currentBinding.instanceId);
        if (instance) {
          setSelectedInstance({
            value: instance._id,
            label: `${instance.appName || instance.name}${instance.customDomain ? ` (${instance.customDomain})` : ` (${instance.subdomain}.zoobbe.com)`}`
          });
        } else {
          // Instance not in available list (bound by another admin)
          // Use instanceDetails from currentBinding
          const details = data.currentBinding.instanceDetails;
          if (details) {
            setSelectedInstance({
              value: details._id,
              label: `${details.appName || details.name}${details.customDomain ? ` (${details.customDomain})` : ` (${details.subdomain}.zoobbe.com)`}`
            });
          }
        }
      } else {
        setSelectedInstance({ value: 'none', label: 'None (Default Zoobbe)' });
      }
    } catch (error) {
      console.error('Error loading white label instances:', error);
      dispatch(showSnackBar({
        message: 'Failed to load white label options',
        type: 'error'
      }));
    }
  };

  const handleSelectionChange = (option) => {
    const newValue = option.value;

    // If there's an existing binding and user is changing it, show confirmation
    if (currentBinding?.instanceId && newValue !== selectedInstance?.value) {
      setPendingInstance(option);
      setShowConfirmModal(true);
    } else {
      // No existing binding or same selection, apply directly
      applyBinding(newValue);
    }
  };

  const applyBinding = async (instanceId) => {
    try {
      setSaving(true);

      if (instanceId === 'none') {
        // Unbind workspace
        await workspaceWhiteLabelService.unbindWorkspace(workspace.shortId);
        setCurrentBinding(null);
        setSelectedInstance({ value: 'none', label: 'None (Default Zoobbe)' });
        dispatch(showSnackBar({
          message: 'Workspace unbound from white label instance',
          type: 'success'
        }));
      } else {
        // Bind workspace to selected instance
        const result = await workspaceWhiteLabelService.bindWorkspace(
          workspace.shortId,
          instanceId
        );
        setCurrentBinding(result.binding);
        const instance = availableInstances.find(i => i._id === instanceId);
        if (instance) {
          setSelectedInstance({
            value: instance._id,
            label: `${instance.appName || instance.name}${instance.customDomain ? ` (${instance.customDomain})` : ` (${instance.subdomain}.zoobbe.com)`}`
          });
        }
        dispatch(showSnackBar({
          message: 'Workspace successfully bound to white label instance',
          type: 'success'
        }));
      }

      setShowConfirmModal(false);
      setPendingInstance(null);
    } catch (error) {
      console.error('Error updating white label binding:', error);
      dispatch(showSnackBar({
        message: error.response?.data?.message || 'Failed to update white label binding',
        type: 'error'
      }));
      // Revert selection on error
      if (currentBinding?.instanceId) {
        const instance = availableInstances.find(i => i._id === currentBinding.instanceId);
        if (instance) {
          setSelectedInstance({
            value: instance._id,
            label: `${instance.appName || instance.name}${instance.customDomain ? ` (${instance.customDomain})` : ` (${instance.subdomain}.zoobbe.com)`}`
          });
        }
      } else {
        setSelectedInstance({ value: 'none', label: 'None (Default Zoobbe)' });
      }
    } finally {
      setSaving(false);
    }
  };

  const handleConfirmChange = () => {
    if (pendingInstance) {
      applyBinding(pendingInstance.value);
    }
  };

  const handleCancelChange = () => {
    setShowConfirmModal(false);
    setPendingInstance(null);
    // Revert to current selection
    if (currentBinding?.instanceId) {
      const instance = availableInstances.find(i => i._id === currentBinding.instanceId);
      if (instance) {
        setSelectedInstance({
          value: instance._id,
          label: `${instance.appName || instance.name}${instance.customDomain ? ` (${instance.customDomain})` : ` (${instance.subdomain}.zoobbe.com)`}`
        });
      }
    } else {
      setSelectedInstance({ value: 'none', label: 'None (Default Zoobbe)' });
    }
  };

  const getCurrentInstanceName = () => {
    if (!currentBinding?.instanceId) return null;

    // First try to find in available instances
    const instance = availableInstances.find(i => i._id === currentBinding.instanceId);
    if (instance) {
      return instance.appName || instance.name || 'Unknown';
    }

    // If not found, use instanceDetails from currentBinding (bound by another admin)
    if (currentBinding.instanceDetails) {
      return currentBinding.instanceDetails.appName || currentBinding.instanceDetails.name || 'Unknown';
    }

    return 'Unknown';
  };

  const getPendingInstanceName = () => {
    if (pendingInstance?.value === 'none') return 'None (Default Zoobbe)';
    return pendingInstance?.label || 'Unknown';
  };

  // Admins can always unbind an existing binding, even without a verified
  // custom domain. `canEditBinding` gates *binding* (needs a verified domain);
  // unbinding must stay available so users don't get trapped on a stale binding.
  const hasBinding = !!currentBinding?.instanceId;
  const canEditSelector = isAdmin && (canEditBinding || hasBinding);

  // Prepare options for SearchSelect. When the user can't create new bindings
  // (no verified domain) but has an existing one, only offer the current
  // instance + "None" so they can unbind but not bind to a domain-less instance.
  const bindableInstances = canEditBinding
    ? availableInstances
    : availableInstances.filter(instance => instance._id === currentBinding?.instanceId);

  const selectOptions = [
    { value: 'none', label: 'None (Default Zoobbe)' },
    ...bindableInstances.map(instance => ({
      value: instance._id,
      label: `${instance.appName || instance.name}${instance.customDomain ? ` (${instance.customDomain})` : ` (${instance.subdomain}.zoobbe.com)`}`
    }))
  ];

  // Don't show if no instances available and no current binding
  if (availableInstances.length === 0 && !currentBinding) {
    return null;
  }

  return (
    <>
      <div className="white-label-config-section">
        <div className="setting-row">
          <div className="setting-label">
            <h2>White Label Configuration</h2>
            <p>
              {isAdmin
                ? 'Bind this workspace to your white label instance to customize branding and domain.'
                : 'View the white label instance this workspace is bound to.'}
            </p>
          </div>
          <div className="white-label-content">
            {canEditSelector ? (
              <>
                <div className="form-group">
                  <SearchSelect
                    options={selectOptions}
                    onSelect={handleSelectionChange}
                    placeholder="Select white label instance"
                    isGrouped={false}
                    defaultValue={selectedInstance?.value}
                    isDisabled={saving}
                  />
                </div>

                {!canEditBinding && hasBinding && (
                  <div className="config-notice warning">
                    <span className="material-symbols-outlined">warning</span>
                    <div className="notice-content">
                      <strong>Custom Domain Required:</strong> Binding to a white label instance requires a verified custom domain. You can still unbind this workspace (select <em>None</em>), but you won't be able to re-bind until a custom domain is verified.
                    </div>
                  </div>
                )}

                {currentBinding && (
                  <div className="current-binding-info">
                    <div className="info-row">
                      <span className="info-label">Currently bound to:</span>
                      <span className="info-value">{getCurrentInstanceName()}</span>
                    </div>
                    {currentBinding.boundAt && (
                      <div className="info-row">
                        <span className="info-label">Bound on:</span>
                        <span className="info-value">
                          {new Date(currentBinding.boundAt).toLocaleDateString()}
                        </span>
                      </div>
                    )}
                  </div>
                )}

                <div className="config-notice">
                  <span className="material-symbols-outlined">info</span>
                  <div className="notice-content">
                    <strong>Note:</strong> Only workspace admins with active white label instances and verified custom domains can bind workspaces. Binding affects:
                    <ul>
                      <li>Domain access for all workspace members</li>
                      <li>Email notifications (SMTP routing)</li>
                      <li>Branding across the workspace</li>
                    </ul>
                  </div>
                </div>
              </>
            ) : isAdmin && !canEditSelector ? (
              <>
                <div className="current-binding-info read-only">
                  <div className="info-row">
                    <span className="info-label">White Label Instance:</span>
                    <span className="info-value">None (Default Zoobbe)</span>
                  </div>
                </div>

                <div className="config-notice warning">
                  <span className="material-symbols-outlined">warning</span>
                  <div className="notice-content">
                    <strong>Custom Domain Required:</strong> To bind this workspace to a white label instance, you need to have a verified custom domain configured in your white label settings. Currently, you can only view the binding.
                  </div>
                </div>
              </>
            ) : (
              <>
                {currentBinding ? (
                  <div className="current-binding-info read-only">
                    <div className="info-row">
                      <span className="info-label">White Label Instance:</span>
                      <span className="info-value">{getCurrentInstanceName()}</span>
                    </div>
                    {currentBinding.boundAt && (
                      <div className="info-row">
                        <span className="info-label">Bound on:</span>
                        <span className="info-value">
                          {new Date(currentBinding.boundAt).toLocaleDateString()}
                        </span>
                      </div>
                    )}
                  </div>
                ) : (
                  <div className="current-binding-info read-only">
                    <div className="info-row">
                      <span className="info-label">White Label Instance:</span>
                      <span className="info-value">None (Default Zoobbe)</span>
                    </div>
                  </div>
                )}

                <div className="config-notice">
                  <span className="material-symbols-outlined">info</span>
                  <div className="notice-content">
                    <strong>Note:</strong> Only workspace admins can change the white label binding.
                  </div>
                </div>
              </>
            )}
          </div>
        </div>

        {/* Save Status */}
        {saving && isAdmin && (
          <div className="setting-row">
            <div className="setting-label"></div>
            <div className="auto-save-status">
              <span className="save-status saving">
                <span className="material-symbols-outlined">sync</span>
                Saving changes...
              </span>
            </div>
          </div>
        )}
      </div>

      {/* Confirmation Modal */}
      {showConfirmModal && (
        <div className="wl-modal-overlay" onClick={handleCancelChange}>
          <div className="wl-modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="wl-modal-header">
              <div className="wl-modal-title">
                <span className="material-symbols-outlined warning-icon">warning</span>
                <h3>Change White Label Binding?</h3>
              </div>
            </div>
            <div className="wl-modal-body">
              <div className="wl-modal-info">
                <p>
                  This workspace is currently using <strong>{getCurrentInstanceName()}</strong>.
                </p>
                <p>
                  Changing to <strong>{getPendingInstanceName()}</strong> will affect:
                </p>
              </div>
              <ul className="wl-modal-list">
                <li>All member access (must use the new domain)</li>
                <li>Email notifications and branding</li>
                <li>Workspace appearance and settings</li>
              </ul>
              <div className="wl-modal-warning">
                <span className="material-symbols-outlined">info</span>
                <span>This change will be applied immediately for all workspace members.</span>
              </div>
            </div>
            <div className="wl-modal-footer">
              <button
                className="wl-btn-cancel"
                onClick={handleCancelChange}
                disabled={saving}
              >
                Cancel
              </button>
              <button
                className="wl-btn-confirm"
                onClick={handleConfirmChange}
                disabled={saving}
              >
                {saving ? 'Changing...' : 'Change Binding'}
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default WorkspaceWhiteLabelConfig;

